import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import * as d3Geo from 'd3-geo';
import './WorldMap.css';

/**
 * WorldMap Component
 * Displays an accurate world map with Germany highlighted in blue outline only
 * Uses d3-geo with TopoJSON/GeoJSON data for proper geographic accuracy
 */
const WorldMap = () => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const width = 2000;
    const height = 1000;
    const svg = d3.select(svgRef.current);

    // Clear previous content
    svg.selectAll('*').remove();

    // Create projection (Natural Earth projection for a nice world map view)
    const projection = d3Geo.geoNaturalEarth1();
    const path = d3Geo.geoPath().projection(projection);

    // Try multiple data sources
    const dataSources = [
      // GeoJSON format
      'https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson',
      // Alternative GeoJSON
      'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json',
      // Natural Earth GeoJSON
      'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson'
    ];

    // eslint-disable-next-line no-unused-vars
    let currentSourceIndex = 0;

    const tryLoadMap = (urlIndex) => {
      if (urlIndex >= dataSources.length) {
        console.error('All data sources failed, using fallback');
        createFallbackMap(svg, path, width, height);
        return;
      }

      const url = dataSources[urlIndex];

      d3.json(url)
        .then((world) => {
          if (!world) {
            throw new Error('No data received');
          }

          // Handle both GeoJSON and TopoJSON formats
          let features = [];
          
          if (world.type === 'FeatureCollection') {
            // It's already GeoJSON
            features = world.features;
          } else if (world.objects) {
            // It's TopoJSON - we need topojson-client but let's try to handle it
            console.warn('TopoJSON format detected but topojson-client not available');
            tryLoadMap(urlIndex + 1);
            return;
          } else if (world.features) {
            features = world.features;
          } else {
            throw new Error('Unknown data format');
          }

          if (!features || features.length === 0) {
            throw new Error('No features found');
          }

          // Focus on Europe: Center on Europe and zoom in
          projection
            .scale(1200)
            .center([10, 52]) // Center on Germany/Europe (longitude, latitude)
            .translate([width / 2, height / 2]);

          // Recreate path with updated projection
          const updatedPath = d3Geo.geoPath().projection(projection);

          // Create a group for all countries
          const g = svg.append('g');

          // European countries list (ISO codes)
          const europeanCountries = [
            'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
            'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 
            'SE', 'GB', 'UK', 'IS', 'NO', 'CH', 'AL', 'BA', 'ME', 'MK', 'RS', 'XK', 'LI',
            'AD', 'MC', 'SM', 'VA', 'DEU', 'AUT', 'BEL', 'BGR', 'HRV', 'CYP', 'CZE', 'DNK',
            'EST', 'FIN', 'FRA', 'GRC', 'HUN', 'IRL', 'ITA', 'LVA', 'LTU', 'LUX', 'MLT',
            'NLD', 'POL', 'PRT', 'ROU', 'SVK', 'SVN', 'ESP', 'SWE', 'GBR', 'ISL', 'NOR', 'CHE'
          ];

          // Debug: Log first few countries to see property structure
          if (features.length > 0) {
            console.log('Sample country properties:', features[0].properties);
            console.log('Looking for France:', features.find(f => {
              const p = f.properties || {};
              const name = p.NAME || p.NAME_LONG || p.name || p.NAME_EN || '';
              return name.toLowerCase().includes('france');
            })?.properties);
          }

          // Draw all countries
          g.selectAll('path')
            .data(features)
            .enter()
            .append('path')
            .attr('d', updatedPath)
            .attr('class', (d) => {
              // Check if country is Germany or European
              const props = d.properties || {};
              
              // Try all possible property name variations
              const isoA2 = props.ISO_A2 || props.iso_a2 || props.ISO2 || props.iso2 || props.ISO || props.iso || '';
              const isoA3 = props.ISO_A3 || props.iso_a3 || props.ISO3 || props.iso3 || '';
              const name = (props.NAME || props.NAME_LONG || props.name || props.NAME_EN || props.ADMIN || '').toLowerCase();
              
              // Germany - bright blue (check multiple ways)
              if (isoA2 === 'DE' || isoA3 === 'DEU' || 
                  name.includes('germany') || 
                  name.includes('deutschland')) {
                console.log('Found Germany:', props);
                return 'country-path germany-highlight';
              }
              
              // Other European countries - light blue
              // Check ISO codes
              if (europeanCountries.includes(isoA2) || europeanCountries.includes(isoA3)) {
                const countryName = props.NAME || props.NAME_LONG || props.name || props.NAME_EN || props.ADMIN || 'Unknown';
                const countryCode = isoA2 || isoA3 || 'Unknown';
                if (countryCode !== 'Unknown') {
                  console.log('Found European country by ISO:', countryCode, countryName !== 'Unknown' ? countryName : '');
                }
                return 'country-path europe-highlight';
              }
              
              // Also check by name for common European countries
              const europeanNames = [
                'france', 'spain', 'italy', 'poland', 'netherlands', 'belgium', 'greece',
                'portugal', 'czech', 'hungary', 'sweden', 'austria', 'switzerland',
                'denmark', 'finland', 'ireland', 'norway', 'romania', 'bulgaria',
                'croatia', 'slovakia', 'slovenia', 'lithuania', 'latvia', 'estonia'
              ];
              
              if (europeanNames.some(euName => name.includes(euName))) {
                const countryName = props.NAME || props.NAME_LONG || props.name || props.NAME_EN || props.ADMIN || 'Unknown';
                if (countryName !== 'Unknown') {
                  console.log('Found European country by name:', countryName);
                }
                return 'country-path europe-highlight';
              }
              
              return 'country-path';
            })
            .attr('data-country', (d) => {
              const props = d.properties || {};
              return props.NAME || props.NAME_LONG || props.name || props.NAME_EN || 'Unknown';
            });

          setMapLoaded(true);
          console.log('World map loaded successfully with', features.length, 'countries');
        })
        .catch((error) => {
          console.error(`Error loading world map from source ${urlIndex + 1}:`, error);
          // Try next source
          tryLoadMap(urlIndex + 1);
        });
    };

    // Start loading
    tryLoadMap(0);
  }, []);

  const createFallbackMap = (svg, path, width, height) => {
    // Fallback: Create a simple message
    const g = svg.append('g');
    
    g.append('text')
      .attr('x', width / 2)
      .attr('y', height / 2)
      .attr('text-anchor', 'middle')
      .attr('class', 'country-path')
      .style('font-size', '24px')
      .style('fill', '#022d54')
      .text('Unable to load world map data');
    
    setMapLoaded(true);
  };

  return (
    <div ref={containerRef} className="world-map-container">
      <svg
        ref={svgRef}
        viewBox="0 0 2000 1000"
        className="world-map-svg"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      />
      {!mapLoaded && (
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          color: '#022d54',
          fontSize: '14px',
          pointerEvents: 'none'
        }}>
          Loading world map...
        </div>
      )}
    </div>
  );
};

export default WorldMap;
